import { AppTabs } from 'components';
import { useDocumentTitle } from 'hooks';
import * as React from 'react';
import { makeStyles, Typography } from '@material-ui/core';

enum DASHBOARD_TABS {
  COMMERCIAL = 'commercial',
  CUSTOMER_SUPPORT = 'customer-success',
  EMPLOYMENT = 'employment',
  EXECUTIVE = 'executive',
  FINANCE = 'finance',
  QUALITY_APPS = 'quality_apps',
  JIM = 'jobsinmalta',
  NPS = 'nps',
  MARKETING = 'marketing',
  RECRUITMENT = 'recruitment',
  PAYROLL = 'payroll',
  PROACT = 'proact',
  PRODUCT = 'product',
  SIM = 'sim',
  JIRA = 'jira',
}

export const DashboardsList = [
  {
    path: `/dashboards#${DASHBOARD_TABS.COMMERCIAL}`,
    value: DASHBOARD_TABS.COMMERCIAL,
    label: 'Commercial',
    description:
      'Provides our Client Team with visibility to how we are advancing towards achieving our current year targets. Monitor progress, engagement activities, and make data-driven decisions that enhance sales strategies and drive sales performance to new heights.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.CUSTOMER_SUPPORT}`,
    value: DASHBOARD_TABS.CUSTOMER_SUPPORT,
    label: 'Customer Support',
    description: 'Statistics and insights from the customer support team.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.EXECUTIVE}`,
    value: DASHBOARD_TABS.EXECUTIVE,
    label: 'Executive Recruitment',
    description: 'Insights into executive recruitment.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.EMPLOYMENT}`,
    value: DASHBOARD_TABS.EMPLOYMENT,
    label: 'Employment Data',
    description: 'Insights into local employment statistics.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.FINANCE}`,
    value: DASHBOARD_TABS.FINANCE,
    label: 'Finance',
    description: 'Insights into finances.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.JIM}`,
    value: DASHBOARD_TABS.JIM,
    label: 'jobsinmalta',
    description: 'Insights into Jobsinmalta listings, sales and clients.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.JIRA}`,
    value: DASHBOARD_TABS.JIRA,
    label: 'JIRA',
    description: 'Insights into Jira issues and projects.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.MARKETING}`,
    value: DASHBOARD_TABS.MARKETING,
    label: 'Marketing',
    description: 'Insights into our marketing efforts.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.NPS}`,
    value: DASHBOARD_TABS.NPS,
    label: 'Net Promoter Score (NPS)',
    description: 'All KonnektTalexio NPS results in one place.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.PAYROLL}`,
    value: DASHBOARD_TABS.PAYROLL,
    label: 'Payroll Service',
    description: 'Data from our Payroll Service and HR Admin Support team.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.PROACT}`,
    value: DASHBOARD_TABS.PROACT,
    label: 'PROACT',
    description:
      'Priority, Revenue, Outreach, and Contact Timing. A dashboard to classify clients and optimise outreach.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.PRODUCT}`,
    value: DASHBOARD_TABS.PRODUCT,
    label: 'Product',
    description: 'Insights and metrics into Talexio.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.QUALITY_APPS}`,
    value: DASHBOARD_TABS.QUALITY_APPS,
    label: 'Quality Applications',
    description:
      'Data and insights into quality applications from our Konnekt Marketing efforts.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.RECRUITMENT}`,
    value: DASHBOARD_TABS.RECRUITMENT,
    label: 'Recruitment Service',
    description: 'Insights from our recruitment team.',
  },
  {
    path: `/dashboards#${DASHBOARD_TABS.SIM}`,
    value: DASHBOARD_TABS.SIM,
    label: 'Salariesinmalta',
    description:
      'Information on salary benchmarks and our salary benchmarking platform.',
  },
];

const useStyles = makeStyles((theme) => ({
  pageHeading: {
    marginBottom: theme.spacing(2),
  },
  iframe: {
    border: 'none',
    width: '100%',
    height: '98vh',
    '@supports (height: 100dvh)': {
      height: 'calc(100dvh - 140px)',
    },
  },
}));

const renderDashboard = (activeTab: string, iframeClass: string) => {
  const config = {
    [DASHBOARD_TABS.JIM]: {
      title: 'JIM',
      src: 'https://lookerstudio.google.com/embed/reporting/cc7806be-8d12-4fb6-be25-0881cb104deb',
    },
    [DASHBOARD_TABS.COMMERCIAL]: {
      title: 'Commercial',
      src: 'https://lookerstudio.google.com/embed/reporting/765e896a-c8da-4910-881b-2b1a94deb547/page/p_oiog2a12dd',
    },
    [DASHBOARD_TABS.CUSTOMER_SUPPORT]: {
      title: 'CS Dashboard',
      src: 'https://lookerstudio.google.com/embed/reporting/f4670077-7b05-4ece-821c-14719a361954/page/p_514j1iy6xc',
    },
    [DASHBOARD_TABS.FINANCE]: {
      title: 'Finance Dashboard',
      src: 'https://lookerstudio.google.com/embed/reporting/ec9f061b-96e5-423d-9ee6-3a481e86fb70/page/p_voyzmyq9md',
    },
    [DASHBOARD_TABS.PROACT]: {
      title: 'PROACT Dashboard',
      src: 'https://lookerstudio.google.com/embed/reporting/9bd11dd5-4889-4736-8ca8-a306d284bf32/page/p_ktdlam3bcd',
    },
    [DASHBOARD_TABS.JIRA]: {
      title: 'JIRA Dashboard',
      src: 'https://lookerstudio.google.com/embed/reporting/6c5716a7-e85c-4f7b-90d3-50ca5103924c/page/vw3OF',
    },
    [DASHBOARD_TABS.PRODUCT]: {
      title: 'Product Dashboard',
      src: 'https://lookerstudio.google.com/embed/reporting/d3210d09-42eb-48f3-9eaa-8cf6e3e79334/page/0aUwC',
    },
    [DASHBOARD_TABS.MARKETING]: {
      title: 'Marketing',
      src: 'https://lookerstudio.google.com/embed/reporting/11636f22-835b-4023-88e4-3df8b4c91741/page/0aUwC',
    },
    [DASHBOARD_TABS.RECRUITMENT]: {
      title: 'Recruiter Dashboard',
      src: 'https://datastudio.google.com/embed/reporting/1GZ7gQMEW8iBRjEnBNUp0943umC47UmCb/page/xEHDB',
    },
    [DASHBOARD_TABS.NPS]: {
      title: 'NPS Dashboard',
      src: 'https://lookerstudio.google.com/embed/reporting/5559d0c3-a019-438a-b0db-3b70067de737/page/p0aUwC',
    },
    [DASHBOARD_TABS.SIM]: {
      title: 'Salariesinmalta',
      src: 'https://lookerstudio.google.com/embed/reporting/4b1b422d-9b04-41db-badc-3f2947b1035b/page/taLPD',
    },
    [DASHBOARD_TABS.QUALITY_APPS]: {
      title: 'Quality Applications',
      src: 'https://lookerstudio.google.com/embed/reporting/14d0e31f-1cc7-47bd-a233-04f57814518f/page/0aUwC',
    },
    [DASHBOARD_TABS.EXECUTIVE]: {
      title: 'Executive Recruitment',
      src: 'https://lookerstudio.google.com/embed/reporting/99f9fec9-ff5d-4aef-9fc5-658313626a2a/page/l3oyC',
    },
    [DASHBOARD_TABS.EMPLOYMENT]: {
      title: 'Employment Data',
      src: 'https://lookerstudio.google.com/embed/reporting/e866bde0-8966-43c3-a64f-da022a1342a7/page/PWr7C',
    },
    [DASHBOARD_TABS.PAYROLL]: {
      title: 'Payroll Service',
      src: 'https://lookerstudio.google.com/embed/reporting/7d4eeb71-277a-480a-a8e8-e6d052d7a83c/page/tfLzC',
    },
  };

  const dashboard = config[activeTab as keyof typeof config];

  if (!dashboard) {
    return (
      <Typography variant="body2" color="textSecondary">
        This dashboard is not yet available.
      </Typography>
    );
  }

  return (
    <iframe
      title={dashboard.title}
      src={dashboard.src}
      className={iframeClass}
      allowFullScreen
    />
  );
};

export const DashboardsView: React.FC = () => {
  useDocumentTitle('Dashboards');
  const classes = useStyles();

  return (
    <>
      <Typography
        variant="h5"
        component={'h1' as React.ElementType}
        className={classes.pageHeading}
      >
        Dashboards
      </Typography>
      <AppTabs tabs={DashboardsList}>
        {(activeTab) => renderDashboard(activeTab, classes.iframe)}
      </AppTabs>
    </>
  );
};
